import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getPaymentMethods, createCardSetupSession, deletePaymentMethod, setDefaultPaymentMethod, syncSavedCard } from '../services/api';
import { Button } from './ui/button';
import { CreditCard, Plus, Trash2, Star, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BRAND_LABELS = { visa: 'Visa', mastercard: 'Mastercard', amex: 'American Express', discover: 'Discover', diners: 'Diners Club', jcb: 'JCB', unionpay: 'UnionPay' };

export const PaymentMethodsSection = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const fetchCards = useCallback(async () => {
    try {
      const data = await getPaymentMethods();
      setCards(data.payment_methods);
    } catch {
      toast.error('Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  useEffect(() => {
    const status = searchParams.get('card_setup');
    const sessionId = searchParams.get('setup_session_id');
    if (status === 'success' && sessionId) {
      syncSavedCard(sessionId)
        .then(() => {
          toast.success('Card saved successfully!');
          fetchCards();
        })
        .catch((err) => {
          toast.error(err.response?.data?.detail || 'Failed to save card');
        });
    } else if (status === 'cancelled') {
      toast.info('Card setup cancelled');
    }
    if (status) {
      searchParams.delete('card_setup');
      searchParams.delete('setup_session_id');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, fetchCards]);

  const handleAddCard = async () => {
    setAdding(true);
    try {
      const { url } = await createCardSetupSession(window.location.origin);
      window.location.href = url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start card setup');
      setAdding(false);
    }
  };

  const handleDelete = async (pmId) => {
    setBusyId(pmId);
    try {
      await deletePaymentMethod(pmId);
      toast.success('Card removed');
      setCards((prev) => prev.filter((c) => c.id !== pmId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove card');
    } finally {
      setBusyId(null);
    }
  };

  const handleSetDefault = async (pmId) => {
    setBusyId(pmId);
    try {
      await setDefaultPaymentMethod(pmId);
      toast.success('Default card updated');
      setCards((prev) => prev.map((c) => ({ ...c, is_default: c.id === pmId })));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update default card');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#EBE8DF] p-6 mb-8" data-testid="payment-methods-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <CreditCard className="w-6 h-6 text-[#D97757]" />
          <h2 className="font-heading text-lg font-semibold text-[#2C3329]">Payment Methods</h2>
        </div>
        <Button
          onClick={handleAddCard}
          disabled={adding}
          size="sm"
          className="bg-[#D97757] text-white rounded-full hover:bg-[#C56547]"
          data-testid="add-payment-method-button"
        >
          {adding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
          Add Card
        </Button>
      </div>

      {loading ? (
        <div className="text-[#596652] text-sm animate-pulse py-4">Loading cards...</div>
      ) : cards.length === 0 ? (
        <p className="text-[#596652] text-sm" data-testid="no-payment-methods">
          No saved cards yet. Add a card for faster checkout when renting items.
        </p>
      ) : (
        <div className="space-y-3">
          {cards.map((card) => (
            <div
              key={card.id}
              className="flex items-center justify-between p-3 rounded-xl border border-[#EBE8DF] bg-[#FDFBF7]"
              data-testid={`payment-method-${card.last4}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-7 bg-[#2C3329] rounded flex items-center justify-center">
                  <CreditCard className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#2C3329]">
                    {BRAND_LABELS[card.brand] || card.brand} •••• {card.last4}
                    {card.is_default && (
                      <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#D97757]/10 text-[#D97757]" data-testid="default-card-badge">
                        <Star className="w-3 h-3" /> Default
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-[#596652]">Expires {String(card.exp_month).padStart(2, '0')}/{card.exp_year}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!card.is_default && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSetDefault(card.id)}
                    disabled={busyId === card.id}
                    className="border-[#EBE8DF] rounded-full text-xs"
                    data-testid={`set-default-${card.last4}`}
                  >
                    Set Default
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(card.id)}
                  disabled={busyId === card.id}
                  className="text-[#596652] hover:text-red-600"
                  data-testid={`delete-card-${card.last4}`}
                >
                  {busyId === card.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
